import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmptyState from '../shared/EmptyState';
import Icon from '../shared/Icon';
import PhotoCredit from '../shared/PhotoCredit';
import SeeAllLink from '../shared/SeeAllLink';
import AdultAvatar from '../shared/AdultAvatar';
import CardGroup, { STRIP_SLOT } from './CardGroup';
import HomeSectionHeader from './HomeSectionHeader';
import { FeedCardSkeleton } from './DashboardSkeleton';
import { renderFeedCard } from './renderFeedCard';
import { isDismissed } from '../../utils/composeWhatsOnSections';
import { feedItemKey } from '../../utils/feedItemKey';
import { autoNormalizeCardData } from '../../utils/normalizeCardData';
import { resizeForSurface } from '../../utils/imageSource';

const CARD_LIMIT = 6;
const ROW_LIMIT = 3;
const POST_TYPES = new Set(['trip', 'album', 'collection', 'board']);
const CARD_IMAGE_SIZING = { surface: 'medium', sizes: '(max-width: 640px) 80vw, 360px' };
const CARD_SLOT = 'h-full';

/** A friend's visit as one row: who, where, how they rated it, when. */
function FriendRow({ item }) {
  const { t } = useTranslation('home');
  const normalized = autoNormalizeCardData(item);
  const photo = normalized.carousel?.images?.[0]?.url || item.cover_photo || null;
  const thumb = photo ? resizeForSurface(photo, 'thumb') : null;
  const rating = normalized.typeData?.rating;
  const body = (
    <>
      <AdultAvatar name={normalized.creator?.name} profilePicture={normalized.creator?.avatar} size="sm" />
      <p className="flex-1 min-w-0 text-sm text-content-primary leading-snug">
        <span className="font-semibold">{normalized.creator?.name || t('inspiration.someone')}</span>
        {' '}{t('inspiration.visited')}{' '}
        <span className="font-semibold">{normalized.title}</span>
        {rating ? (
          <span className="inline-flex items-center gap-0.5 ml-1.5 text-content-secondary tabular-nums">
            <Icon name="starFilled" className="w-3.5 h-3.5 text-accent" />{Number(rating).toFixed(1)}
          </span>
        ) : null}
      </p>
      <span className="hidden sm:block text-xs text-content-secondary flex-shrink-0">{normalized.timeAgo}</span>
      {thumb && (
        <div className="relative w-14 h-[42px] rounded-lg overflow-hidden flex-shrink-0 bg-background">
          <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
          <PhotoCredit src={thumb} size="xs" interactive={false} />
        </div>
      )}
    </>
  );
  const className = 'flex items-center gap-3 py-2.5 border-t border-border first:border-t-0 -mx-4 px-4 can-hover:hover:bg-surface-secondary transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-light';
  return normalized.detailUrl ? <Link to={normalized.detailUrl} className={className}>{body}</Link> : <div className={className}>{body}</div>;
}

/**
 * "Travel inspiration": great trips, shared — people you follow and travellers
 * like you, in one grid (the creator row tells them apart), posts about the
 * hero trip's destination first ("For your Lisbon trip"). The smaller signals
 * from people you follow — their visits — are the rows beneath the cards.
 *
 * Folds the old Friends' Activity strip in: its posts lead the cards, its
 * visits become the rows. Both lists load lazily on their own sentinels.
 */
export default function TravelInspirationSection({ data, friends, buildActions, modals, dismissedIds, onRetry, onRetryFriends, sectionRef, friendsRef }) {
  const { t } = useTranslation('home');

  const { cards, rows, forTrip } = useMemo(() => {
    const dismissed = (item) => dismissedIds?.size > 0 && isDismissed(item, dismissedIds);
    const friendItems = (friends.items || []).filter(i => !dismissed(i) && !i.is_own_content);
    const friendPosts = friendItems.filter(i => POST_TYPES.has(i.content_type));
    const visits = friendItems.filter(i => i.content_type === 'activity').slice(0, ROW_LIMIT);
    const seen = new Set();
    const out = [];
    for (const item of [...(data.items || []).filter(i => i._forTrip), ...friendPosts, ...(data.items || []).filter(i => !i._forTrip)]) {
      if (dismissed(item)) continue;
      const key = feedItemKey(item);
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      out.push(item);
      if (out.length >= CARD_LIMIT) break;
    }
    return { cards: out, rows: visits, forTrip: out.find(i => i._forTrip)?._forTrip || null };
  }, [data.items, friends.items, dismissedIds]);

  const loading = (data.loading || !data.loaded) && !data.error;
  const friendsLoading = (friends.loading || !friends.loaded) && !friends.error;

  if (!loading && !data.error && cards.length === 0 && !friendsLoading && rows.length === 0) return null;

  return (
    <section ref={sectionRef} aria-labelledby="home-inspiration-heading" className="scroll-mt-20">
      <HomeSectionHeader
        id="home-inspiration-heading"
        title={t('inspiration.title')}
        subtitle={forTrip ? t('inspiration.subtitleTrip', { place: forTrip.name }) : t('inspiration.subtitle')}
        action={!loading && cards.length > 0 && <SeeAllLink to="/social-feed?types=trips%2Calbums%2Ccollections%2Cboards">{t('inspiration.seeAll')}</SeeAllLink>}
      />
      {loading ? (
        <CardGroup cols={3}>
          <span className="sr-only" role="status">{t('dashboardSkeleton.loadingFeed')}</span>
          {Array.from({ length: 3 }).map((_, i) => <FeedCardSkeleton key={i} />)}
        </CardGroup>
      ) : data.error ? (
        <EmptyState.Error message={typeof data.error === 'string' ? data.error : undefined} onRetry={onRetry} />
      ) : cards.length > 0 ? (
        <CardGroup cols={3} className="animate-fade-in">
          {cards.map((item, index) => (
            <div key={feedItemKey(item) || index} className={`${STRIP_SLOT} relative`}>
              {item._forTrip && (
                <span className="absolute top-2.5 left-2.5 z-raised px-2 h-6 inline-flex items-center rounded-full bg-coral-light text-coral-dark text-xs font-bold pointer-events-none">
                  {t('inspiration.forTrip', { place: item._forTrip.name })}
                </span>
              )}
              {renderFeedCard(item, index, { buildActions, modals, imageSizing: CARD_IMAGE_SIZING, slotClassName: CARD_SLOT })}
            </div>
          ))}
        </CardGroup>
      ) : null}

      {/* The friends list's own sentinel: it loads when this scrolls into view. */}
      <div ref={friendsRef} className="mt-4">
        {friends.error ? (
          <EmptyState.Error message={typeof friends.error === 'string' ? friends.error : undefined} onRetry={onRetryFriends} />
        ) : rows.length > 0 ? (
          <div className="bg-surface rounded-card border border-border px-4 pt-3 pb-1 animate-fade-in">
            <p className="text-[11px] font-bold tracking-[.06em] uppercase text-content-secondary mb-1">{t('inspiration.alsoFromFollows')}</p>
            {rows.map((item, i) => <FriendRow key={feedItemKey(item) || i} item={item} />)}
          </div>
        ) : null}
      </div>
    </section>
  );
}
